import { useEffect, useRef, useState } from "react";
import { ImagePlus, Film, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";

const IMG_TYPES = /^image\//;
const VID_TYPES = /^video\//;

const formatSize = (bytes) => {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

export default function AttachmentUpload({ onChange, disabled = false }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleFile = (f) => {
    if (!f) return;
    if (!IMG_TYPES.test(f.type) && !VID_TYPES.test(f.type)) return;
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
    onChange?.(f);
  };

  const clear = () => {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    onChange?.(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const isVideo = file && VID_TYPES.test(file.type);

  return (
    <div
      className="bg-white border border-sage rounded-2xl shadow-ambient p-6 sm:p-8"
      data-testid="attachment-upload"
    >
      <div className="mb-5 flex items-center justify-between">
        <div>
          <p className="accent-label mb-1">Step 02 · optional</p>
          <h3 className="font-display text-xl sm:text-2xl text-ink">
            Add a screenshot or video
          </h3>
        </div>
      </div>

      {!file ? (
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (disabled) return;
            const f = e.dataTransfer.files?.[0];
            if (f) handleFile(f);
          }}
          className={[
            "flex flex-col items-center justify-center gap-3 text-center",
            "rounded-xl border-2 border-dashed cursor-pointer",
            "px-6 py-10 transition-colors duration-200",
            dragOver
              ? "border-[#D47A6A] bg-[#F4F1EA]/80"
              : "border-sage bg-sand/60 hover:bg-sand",
            disabled && "opacity-60 cursor-not-allowed",
          ].join(" ")}
          data-testid="attachment-dropzone"
        >
          <span className="h-10 w-10 rounded-full bg-white grid place-items-center border border-sage text-terracotta">
            <ImagePlus className="w-5 h-5" strokeWidth={1.6} />
          </span>
          <p className="text-sm text-ink">
            <span className="font-medium">Click to browse</span> or drop an image/video
          </p>
          <p className="text-xs text-ink-muted">png, jpg, mp4, mov · up to 50 MB</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            data-testid="attachment-upload-input"
            disabled={disabled}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </label>
      ) : (
        <div
          className="rounded-xl bg-sand p-3 sm:p-4"
          data-testid="attachment-preview"
        >
          <div className="rounded-lg overflow-hidden bg-black/5 max-h-72 flex items-center justify-center">
            {isVideo ? (
              <video
                src={preview}
                className="max-h-72 w-full object-contain bg-black"
                controls
              />
            ) : (
              <img
                src={preview}
                alt="Attachment preview"
                className="max-h-72 w-full object-contain"
              />
            )}
          </div>
          <div className="flex items-center justify-between gap-4 mt-3">
            <div className="flex items-center gap-3 min-w-0">
              <span className="h-8 w-8 rounded-lg bg-white grid place-items-center border border-sage text-terracotta">
                {isVideo ? (
                  <Film className="w-4 h-4" strokeWidth={1.6} />
                ) : (
                  <ImagePlus className="w-4 h-4" strokeWidth={1.6} />
                )}
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink truncate">{file.name}</p>
                <p className="text-xs text-ink-muted">
                  {isVideo ? "Video" : "Screenshot"} · {formatSize(file.size)}
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              onClick={clear}
              data-testid="remove-attachment-button"
              className="rounded-xl text-[#A85346] hover:bg-[#D47A6A]/10 hover:text-[#A85346]"
            >
              <Trash2 className="w-4 h-4 mr-1.5" strokeWidth={1.6} />
              Remove
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
